import { Package, TrendingUp, Target, Users, BarChart3, Heart, Megaphone, Mail, LineChart, ShoppingCart } from "lucide-react";
import SubServicePageLayout from "@/components/SubServicePageLayout";

const services = [
  {
    name: "Brand-Led Acquisition",
    icon: Target,
    components: [
      "Performance creative development",
      "Influencer and creator partnerships",
      "Brand awareness campaigns",
    ],
    outcome: "Acquisition that builds brand equity, not just transactions.",
  },
  {
    name: "Channel Diversification",
    icon: TrendingUp,
    components: [
      "Multi-platform paid media strategy",
      "Retail and wholesale expansion",
      "Marketplace presence optimization",
    ],
    outcome: "Reduce single-channel dependency and risk.",
  },
  {
    name: "Community Building",
    icon: Users,
    components: [
      "Social media strategy and management",
      "User-generated content programs",
      "Ambassador and loyalty programs",
    ],
    outcome: "Build a community that drives organic growth.",
  },
  {
    name: "Retention Architecture",
    icon: Heart,
    components: [
      "Email and SMS lifecycle programs",
      "Subscription and membership models",
      "Post-purchase experience optimization",
    ],
    outcome: "Turn one-time buyers into lifetime customers.",
  },
  {
    name: "Unit Economics Optimization",
    icon: BarChart3,
    components: [
      "CAC and LTV modeling",
      "Contribution margin analysis",
      "Pricing and promotion strategy",
    ],
    outcome: "Profitable growth, not just revenue growth.",
  },
  {
    name: "Fulfillment & Operations",
    icon: Package,
    components: [
      "3PL and fulfillment optimization",
      "Inventory planning and forecasting",
      "Customer experience improvements",
    ],
    outcome: "Operations that scale without breaking.",
  },
];

const processSteps = [
  {
    number: "01",
    title: "Brand & Business Audit",
    description: "Understand your brand positioning, unit economics, and growth constraints.",
  },
  {
    number: "02",
    title: "Growth Strategy",
    description: "Develop a multi-channel strategy that balances acquisition with profitability.",
  },
  {
    number: "03",
    title: "Campaign Execution",
    description: "Launch performance campaigns across paid, organic, and owned channels.",
  },
  {
    number: "04",
    title: "Community & Retention",
    description: "Build retention systems and community programs that compound growth.",
  },
  {
    number: "05",
    title: "Scale Sustainably",
    description: "Expand channels, optimize operations, and maintain brand integrity at scale.",
  },
];

const technologyCategories = [
  { title: "Advertising", icon: Megaphone, items: ["Meta Ads", "TikTok Ads", "Pinterest Ads", "Snapchat Ads"] },
  { title: "Email & SMS", icon: Mail, items: ["Klaviyo", "Attentive", "Postscript", "Yotpo"] },
  { title: "Analytics", icon: LineChart, items: ["Triple Whale", "Northbeam", "Rockerbox", "GA4"] },
  { title: "Commerce", icon: ShoppingCart, items: ["Shopify Plus", "Recharge", "Gorgias", "Zendesk"] },
];

const faqs = [
  {
    question: "What makes D2C growth different from general e-commerce?",
    answer: "D2C brands compete on brand, not just price. We focus on building sustainable competitive advantages through community, retention, and brand equity. Not just optimizing ads.",
  },
  {
    question: "How do you balance brand building with performance marketing?",
    answer: "We believe they're not in conflict. Strong creative that builds brand can also perform. We measure both brand lift and direct response metrics.",
  },
  {
    question: "What revenue stage D2C brands do you work with?",
    answer: "Our sweet spot is $2M-$50M in annual revenue. Brands that have found product-market fit and are ready to scale without sacrificing profitability.",
  },
  {
    question: "How do you handle the rising cost of paid acquisition?",
    answer: "By diversifying channels, improving retention, and building owned audiences. Paid acquisition is part of the mix, not the entire strategy.",
  },
  {
    question: "Do you work with subscription D2C brands?",
    answer: "Yes. Subscription economics are different, and we adapt our approach to focus on subscriber acquisition cost, churn reduction, and LTV optimization.",
  },
];

const qualification = {
  forYou: [
    "You're a D2C brand doing $2M+ in annual revenue",
    "You have a differentiated product and clear brand identity",
    "You want to grow profitably, not just grow",
    "You understand that brand and performance work together",
  ],
  notForYou: [
    "You're competing primarily on price",
    "You want to scale at all costs regardless of profitability",
    "You're looking for a quick exit without building real value",
    "You don't have product-market fit yet",
  ],
};

const D2CScale = () => {
  return (
    <SubServicePageLayout
      seoTitle="D2C Scale Services"
      seoDescription="Direct-to-consumer brand growth from startup to scale-up. Channel diversification, brand building, and profitability-focused strategies."
      canonicalUrl="https://xperfomax.com/solutions/d2c-scale"
      category="solutions"
      categoryLabel="Solutions / D2C Scale"
      headline="D2C growth that lasts."
      subheadline="We help direct-to-consumer brands scale from startup to scale-up with sustainable growth strategies that build brand equity and profitability."
      problemTitle="Why most D2C brands struggle to scale."
      problems={[
        "Over-reliance on paid acquisition creates fragile, expensive growth.",
        "Brand building is sacrificed for short-term performance metrics.",
        "Retention is ignored until acquisition costs become unsustainable.",
        "Unit economics cannot support the planned growth trajectory.",
      ]}
      systemTitle="A complete growth system for D2C."
      systemDescription="Acquisition, retention, and brand building working together for sustainable growth."
      services={services}
      processTitle="From audit to sustainable scale."
      processSteps={processSteps}
      technologyCategories={technologyCategories}
      techTitle="Platforms"
      techDescription="We work with the tools that power modern D2C operations."
      qualification={qualification}
      faqs={faqs}
      finalCtaTitle="Ready to scale sustainably?"
      finalCtaDescription="Let's discuss how we can help you grow without sacrificing brand or profitability."
      finalCtaButtonText="Start a serious conversation"
    />
  );
};

export default D2CScale;
